import { Integration } from '@dadohr/shared/domainTypes/integrations';
import { useCallback, useEffect, useState } from 'react';
import useIsMounted from '../../../../../../hooks/useIsMounted';
import { callFirebaseFunction } from '../../../../../../services/firebaseFunctions';
import {
  UkgConnectionStatus,
  UkgCredentials,
  UkgVerificationResult
} from './verification';

/**
 * The `ukg` functions codebase deploys Gen2 callables under these exact names.
 */
const CF_VERIFY_AND_STORE = 'ukg_verifyCredentialsAndStore';
const CF_TEST_CONNECTION = 'ukg_testConnection';
const CF_CONNECTION_STATUS = 'ukg_getConnectionStatus';
const CF_DISCONNECT = 'ukg_disconnect';

/**
 * `Integration.UKG` ('ukg') exists in main-v2's shared source but not yet in the
 * published `@dadohr/shared@1.12.0` this repo installs, so the enum member
 * cannot be referenced here. The literal is the same value the backend writes,
 * which is what `isActiveIntegration` looks up in `configInfos`.
 *
 * TODO(shared): replace with `Integration.UKG` once shared is republished —
 * see the note at the top of `verification.ts`.
 */
export const UKG_INTEGRATION_TYPE = 'ukg' as unknown as Integration;

/**
 * Verifies the five credentials against BOTH UKG transports and stores them
 * only if both pass. On failure the thrown HttpsError carries the per-transport
 * outcome in `details` — parse it with `parseUkgError`.
 */
export const connectUkg = (orgId: string, creds: UkgCredentials) =>
  callFirebaseFunction<{ success: true; verification: UkgVerificationResult }>(
    CF_VERIFY_AND_STORE,
    { orgId, creds }
  );

/** Re-probes the stored credentials. Throws `UKG_ERR_4001` if none are stored. */
export const testUkgConnection = (orgId: string) =>
  callFirebaseFunction<UkgVerificationResult>(CF_TEST_CONNECTION, { orgId });

/** Returns no secrets, so it is safe to call on page load. */
export const getUkgConnectionStatus = (orgId: string) =>
  callFirebaseFunction<UkgConnectionStatus>(CF_CONNECTION_STATUS, { orgId });

/** Removes the stored credentials. */
export const disconnectUkg = (orgId: string) =>
  callFirebaseFunction<{ success: true }>(CF_DISCONNECT, { orgId });

export const useUkgConnectionStatus = (orgId: string) => {
  const isMounted = useIsMounted();
  const [status, setStatus] = useState<UkgConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getUkgConnectionStatus(orgId);
      if (!isMounted.current) {
        return;
      }
      setStatus(result);
      setError(null);
    } catch (err) {
      if (!isMounted.current) {
        return;
      }
      setError(
        err instanceof Error
          ? err
          : new Error('Could not load the UKG Pro connection status.')
      );
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [orgId, isMounted]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { status, loading, error, refresh };
};
